import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Search as SearchIcon, PackageX, BarChart3, Warehouse } from "lucide-react";
import InventoryReports from "./InventoryReports";

const PERIODS = [[7, "7d"], [30, "30d"], [90, "90d"], [180, "180d"]];
const when = (iso) => (iso ? new Date(iso).toLocaleDateString("en-PH", { timeZone: "Asia/Manila" }) : "—");
const qty = (n) => Number(n || 0).toLocaleString("en-PH", { timeZone: "Asia/Manila" });
const peso = (v) => `₱${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const availCls = (a) => a === "no_stock" ? "bg-rose-100 text-rose-700 border-rose-200"
    : a === "phased_out" ? "bg-amber-100 text-amber-800 border-amber-200"
    : "bg-emerald-100 text-emerald-700 border-emerald-200";
const availLabel = (a) => a === "no_stock" ? "No stock" : a === "phased_out" ? "Phased out" : "In stock";

export default function InquiryReports() {
    const { user } = useAuth();
    // Purchasing gets the unmet-demand view only; Inquiry Dept / admin get the full view.
    const fullView = user?.role === "admin" || user?.role === "inquiry_dept";
    const [days, setDays] = useState(30);
    const [most, setMost] = useState([]);
    const [gap, setGap] = useState([]);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState("demand");

    useEffect(() => {
        let alive = true;
        (async () => {
            setLoading(true);
            try {
                const gp = api.get("/inquiry/reports/unavailable-demand", { params: { days, limit: 50 } });
                const lp = api.get("/inquiry/logs", { params: { limit: 150 } });
                const mp = fullView ? api.get("/inquiry/reports/most-searched", { params: { days, limit: 50 } }) : null;
                const [g, lg, m] = await Promise.all([gp, lp, mp]);
                if (!alive) return;
                setGap(Array.isArray(g.data?.items) ? g.data.items : []);
                setLogs(Array.isArray(lg.data?.logs) ? lg.data.logs : []);
                setMost(m && Array.isArray(m.data?.items) ? m.data.items : []);
            } catch (e) {
                if (alive) toast.error(formatApiError(e));
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [days, fullView]);

    return (
        <div className="p-3 md:p-6 space-y-5" data-testid="inquiry-reports-page">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
                <div>
                    <div className="sc-section-title">Inquiry</div>
                    <h1 className="text-xl md:text-2xl font-semibold tracking-tight">Inquiry Reports</h1>
                    <p className="text-sm text-muted-foreground mt-1">What agents are looking for — and what they can't find.</p>
                </div>
                {view === "demand" && (
                <div className="inline-flex rounded-md border border-border overflow-hidden self-start">
                    {PERIODS.map(([val, label], i) => (
                        <button
                            key={val}
                            onClick={() => setDays(val)}
                            className={`px-3 py-1.5 text-sm ${i > 0 ? "border-l border-border" : ""} ${days === val ? "bg-secondary font-medium" : "text-muted-foreground hover:bg-secondary/50"}`}
                            data-testid={`inq-period-${val}`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
                )}
            </div>

            <div className="inline-flex rounded-md border border-border overflow-hidden">
                <button
                    onClick={() => setView("demand")}
                    className={`px-3 py-1.5 text-sm inline-flex items-center gap-1.5 ${view === "demand" ? "bg-secondary font-medium" : "text-muted-foreground hover:bg-secondary/50"}`}
                    data-testid="inqrep-view-demand"
                >
                    <BarChart3 className="w-3.5 h-3.5" /> Demand &amp; Logs
                </button>
                <button
                    onClick={() => setView("inventory")}
                    className={`px-3 py-1.5 text-sm inline-flex items-center gap-1.5 border-l border-border ${view === "inventory" ? "bg-secondary font-medium" : "text-muted-foreground hover:bg-secondary/50"}`}
                    data-testid="inqrep-view-inventory"
                >
                    <Warehouse className="w-3.5 h-3.5" /> Inventory Reports
                </button>
            </div>

            {view === "inventory" && <InventoryReports />}

            {view === "demand" && (<>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                {fullView && (
                <div className="sc-card p-4 md:p-5">
                    <div className="flex items-center gap-2 mb-3">
                        <SearchIcon className="w-4 h-4 text-muted-foreground" />
                        <div className="font-semibold">Most searched items</div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full sc-table min-w-[560px]">
                            <thead>
                                <tr>
                                    <th>Item</th>
                                    <th>Company</th>
                                    <th className="text-right">Inquiries</th>
                                    <th className="text-right">Qty inquired</th>
                                    <th className="text-right">No stock</th>
                                    <th className="text-right">Agents</th>
                                    <th>Last</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && <tr><td colSpan={7} className="text-center py-6 text-muted-foreground">Loading…</td></tr>}
                                {!loading && most.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-muted-foreground">No inquiries in this period.</td></tr>}
                                {!loading && most.map((r) => (
                                    <tr key={r.item_code}>
                                        <td>
                                            <div className="font-mono text-xs">{r.item_code}</div>
                                            <div className="text-xs text-muted-foreground">{r.item_name || ""}</div>
                                        </td>
                                        <td className="text-xs">{r.schema_display_name || "—"}</td>
                                        <td className="sc-num font-semibold">{r.inquiries}</td>
                                        <td className="sc-num">{qty(r.total_qty)}</td>
                                        <td className="sc-num text-rose-600">{r.no_stock_hits || 0}</td>
                                        <td className="sc-num">{r.agent_count}</td>
                                        <td className="text-xs text-muted-foreground whitespace-nowrap">{when(r.last_inquired)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                )}

                <div className="sc-card p-4 md:p-5">
                    <div className="flex items-center gap-2 mb-3">
                        <PackageX className="w-4 h-4 text-rose-500" />
                        <div className="font-semibold">Unmet demand — searched but no stock / phased out</div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full sc-table min-w-[560px]">
                            <thead>
                                <tr>
                                    <th>Item</th>
                                    <th>Company</th>
                                    <th className="text-right">Missed</th>
                                    <th className="text-right">Qty</th>
                                    <th>Reason</th>
                                    <th className="text-right">Agents</th>
                                    <th>Last</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && <tr><td colSpan={7} className="text-center py-6 text-muted-foreground">Loading…</td></tr>}
                                {!loading && gap.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-muted-foreground">No unmet-demand inquiries in this period.</td></tr>}
                                {!loading && gap.map((r) => (
                                    <tr key={r.item_code}>
                                        <td>
                                            <div className="font-mono text-xs">{r.item_code}</div>
                                            <div className="text-xs text-muted-foreground">{r.item_name || ""}</div>
                                        </td>
                                        <td className="text-xs">{r.schema_display_name || "—"}</td>
                                        <td className="sc-num font-semibold text-rose-600">{r.misses}</td>
                                        <td className="sc-num">{qty(r.total_qty)}</td>
                                        <td>
                                            <span className={`inline-block text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border ${r.reason === "phased_out" ? "bg-amber-100 text-amber-800 border-amber-200" : "bg-rose-100 text-rose-700 border-rose-200"}`}>
                                                {r.reason === "phased_out" ? "Phased out" : "No stock"}
                                            </span>
                                        </td>
                                        <td className="sc-num">{r.agent_count}</td>
                                        <td className="text-xs text-muted-foreground whitespace-nowrap">{when(r.last_inquired)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div className="sc-card p-4 md:p-5">
                <div className="font-semibold mb-3">Inquiry log — full history</div>
                <div className="overflow-x-auto">
                    <table className="w-full sc-table" style={{ minWidth: 1240 }}>
                        <thead>
                            <tr>
                                <th>Schema</th>
                                <th>Date &amp; Time</th>
                                <th>Store</th>
                                <th>Sales Agent</th>
                                <th>Item Code / Name</th>
                                <th className="text-right">Qty</th>
                                <th>Status</th>
                                <th>Available in SAP</th>
                                <th>ETA</th>
                                <th className="text-right">Distributors Price</th>
                                <th className="text-right">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan={11} className="text-center py-6 text-muted-foreground">Loading…</td></tr>}
                            {!loading && logs.length === 0 && <tr><td colSpan={11} className="text-center py-6 text-muted-foreground">No inquiries logged.</td></tr>}
                            {!loading && logs.map((l) => (
                                <tr key={l.id}>
                                    <td className="text-xs font-medium">{l.schema_display_name || l.schema_name || "—"}</td>
                                    <td className="text-xs text-muted-foreground whitespace-nowrap">{l.created_at ? new Date(l.created_at).toLocaleString("en-PH", { timeZone: "Asia/Manila" }) : "—"}</td>
                                    <td className="text-xs">{l.store || "—"}</td>
                                    <td className="font-medium">{l.agent}</td>
                                    <td><div className="font-mono text-xs">{l.item_code || "—"}</div><div className="text-xs text-muted-foreground">{l.item_name || ""}</div></td>
                                    <td className="sc-num">{qty(l.quantity)}</td>
                                    <td><span className={`inline-block text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border ${availCls(l.availability)}`}>{l.status_detail || availLabel(l.availability)}</span></td>
                                    <td className="text-xs whitespace-nowrap">{l.sap_total_qty != null ? `${qty(l.sap_total_qty)}pcs (${l.sap_tonality_count || 0} ${(l.sap_tonality_count === 1) ? "tonality" : "tonalities"})` : "—"}</td>
                                    <td className="text-xs text-muted-foreground whitespace-nowrap">{l.eta || "—"}</td>
                                    <td className="sc-num">{l.dealer_price != null ? peso(l.dealer_price) : "—"}</td>
                                    <td className="sc-num">{l.amount != null ? peso(l.amount) : "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <p className="text-xs text-muted-foreground">
                Demand is captured when an agent runs an availability check on an item. "Unmet demand" = items inquired that had no sellable stock or are phased out — a purchasing / range-planning signal. Full history is visible to the Inquiry Department and admin; Purchasing sees only the unmet-demand (no/insufficient-stock) entries.
            </p>

            </>)}
        </div>
    );
}
